import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { getHubItems } from './hubs';

/**
 * A single hub row in the sidebar. Renders either a tiny rail-mode icon
 * tile (when sidebarCollapsed=true) or an expanded button with grouped /
 * flat children. Extracted from AdminLayout.jsx during the iter175
 * layout refactor for maintainability.
 */
export function HubRow({
  hub,
  collapsed,
  expanded,
  active,
  hasFresh,
  onToggle,
  onChildClick,
  isActiveFn,
  getBadgeCount,
  permissions,
  onHoverOpen,
  onHoverClose,
}) {
  const ref = useRef(null);
  const Icon = hub.hubIcon;
  const allItems = getHubItems(hub);
  const visibleItems = allItems.filter(i => !permissions || !i.permission || permissions[i.permission]);
  if (visibleItems.length === 0) return null;

  const onMouseEnter = () => {
    if (!collapsed) return;
    const rect = ref.current?.getBoundingClientRect();
    if (rect) onHoverOpen({ top: rect.top, left: rect.right, height: rect.height });
  };

  // Rail mode: pure icon tile. Click on tile opens flyout too (touch).
  if (collapsed) {
    return (
      <div ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onHoverClose}
        data-testid={`admin-hub-rail-${hub.id}`}
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '44px', height: '44px', borderRadius: '10px',
          background: active ? 'var(--hs-sidebar-hover)' : 'transparent',
          color: active ? 'var(--hs-sidebar-text)' : 'var(--hs-sidebar-text-muted)',
          cursor: 'pointer', transition: 'all 0.15s ease',
          margin: '0 auto',
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'var(--hs-sidebar-hover)'; e.currentTarget.style.color = 'var(--hs-sidebar-text)'; }}
        onMouseOut={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--hs-sidebar-text-muted)';
          }
        }}>
        <Icon size={22} />
        {hasFresh && (
          <span data-testid={`admin-hub-dot-${hub.id}`}
            aria-label="New activity"
            title="New activity"
            style={{
              position: 'absolute', top: 6, right: 6,
              width: 9, height: 9, borderRadius: '50%',
              background: '#F5841F',
              border: '1.5px solid var(--hs-sidebar, #1a1e28)',
              boxShadow: '0 0 0 2px rgba(245,132,31,0.25)',
              pointerEvents: 'none',
            }} />
        )}
      </div>
    );
  }

  // Expanded sidebar: illustrative icon on the left (no colored tile)
  return (
    <div>
      <button onClick={onToggle} data-testid={`admin-hub-${hub.id}`}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
          padding: '9px 12px', borderRadius: '10px', border: 'none',
          background: active ? 'var(--hs-sidebar-hover)' : 'transparent',
          cursor: 'pointer', transition: 'background 0.15s ease',
          textAlign: 'left',
          color: active ? 'var(--hs-sidebar-text)' : 'var(--hs-sidebar-text-muted)',
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--hs-sidebar-hover)'; e.currentTarget.style.color = 'var(--hs-sidebar-text)'; }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--hs-sidebar-text-muted)';
          }
        }}>
        <div style={{
          position: 'relative',
          width: '28px', height: '28px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={24} />
          {hasFresh && (
            <span data-testid={`admin-hub-dot-${hub.id}`}
              aria-label="New activity"
              title="New activity"
              style={{
                position: 'absolute', top: -2, right: -2,
                width: 9, height: 9, borderRadius: '50%',
                background: '#F5841F',
                border: '1.5px solid var(--hs-sidebar, #1a1e28)',
                boxShadow: '0 0 0 2px rgba(245,132,31,0.25)',
              }} />
          )}
        </div>
        <span style={{ flex: 1, fontSize: '14px', fontWeight: 700, color: 'var(--hs-sidebar-text)', letterSpacing: '-0.01em' }}>{hub.label}</span>
        <ChevronDown size={14} style={{ color: 'var(--hs-sidebar-text-muted)', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s ease' }} />
      </button>

      {expanded && (
        <div style={{ marginTop: '2px', marginLeft: '8px', paddingLeft: '20px', borderLeft: '2px solid var(--hs-sidebar-border)' }}>
          {hub.groups ? (
            // Grouped rendering: sub-header + items per group
            hub.groups.map((g) => {
              const groupItems = g.items.filter(i => !permissions || !i.permission || permissions[i.permission]);
              if (groupItems.length === 0) return null;
              return (
                <div key={g.label} style={{ marginTop: 6 }}>
                  <div data-testid={`admin-group-${hub.id}-${g.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                    style={{
                      fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
                      color: 'var(--hs-sidebar-text-muted)', opacity: 0.7,
                      padding: '8px 10px 4px', userSelect: 'none',
                    }}>
                    {g.label}
                  </div>
                  {groupItems.map((item) => (
                    <SidebarLink key={item.href} item={item} isActv={isActiveFn(item.href)} badgeCount={item.badge ? getBadgeCount(item.badge) : null} onClick={onChildClick} />
                  ))}
                </div>
              );
            })
          ) : (
            // Flat rendering for hubs without groups
            visibleItems.map((item) => (
              <SidebarLink key={item.href} item={item} isActv={isActiveFn(item.href)} badgeCount={item.badge ? getBadgeCount(item.badge) : null} onClick={onChildClick} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* Single sidebar link row used by both grouped and flat hub rendering. */
export function SidebarLink({ item, isActv, badgeCount, onClick }) {
  const ItemIcon = item.icon;
  return (
    <Link to={item.href} data-testid={`admin-nav-${item.testId}`}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '7px 10px', borderRadius: '7px',
        textDecoration: 'none', fontSize: '12.5px',
        color: isActv ? 'var(--hs-sidebar-text)' : 'var(--hs-sidebar-text-muted)',
        fontWeight: isActv ? 700 : 500,
        background: isActv ? 'var(--hs-sidebar-active)' : 'transparent',
        transition: 'background 0.12s ease',
      }}
      onMouseEnter={(e) => { if (!isActv) e.currentTarget.style.background = 'var(--hs-sidebar-hover)'; }}
      onMouseLeave={(e) => { if (!isActv) e.currentTarget.style.background = 'transparent'; }}>
      <ItemIcon size={14} strokeWidth={isActv ? 2.2 : 1.7} />
      <span style={{ flex: 1 }}>{item.label}</span>
      {badgeCount !== null && (
        <span style={{ fontSize: '10px', fontWeight: 700, minWidth: '20px', textAlign: 'center', padding: '1px 6px', borderRadius: '999px', background: 'var(--hs-sidebar-hover)', color: 'var(--hs-sidebar-text)' }}>{badgeCount}</span>
      )}
    </Link>
  );
}
